import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, Edit, Trash2, Eye } from 'lucide-react';
import { CategoriaFormModal } from '@/components/CategoriaFormModal';
import { CategoriaViewModal } from '@/components/CategoriaViewModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CategoriaN1 {
  id: string;
  nome: string;
  descricao?: string;
  created_at: string;
  updated_at: string;
}

interface CategoriaN2 {
  id: string;
  id_categoria_n1: string;
  nome: string;
  descricao?: string;
  created_at: string;
  updated_at: string;
}

interface CategoriaN3 {
  id: string;
  id_categoria_n2: string;
  nome: string;
  descricao?: string;
  created_at: string;
  updated_at: string;
}

export default function Categorias() {
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Data states
  const [categoriasN1, setCategoriasN1] = useState<CategoriaN1[]>([]);
  const [categoriasN2, setCategoriasN2] = useState<CategoriaN2[]>([]);
  const [categoriasN3, setCategoriasN3] = useState<CategoriaN3[]>([]);

  // Selection states
  const [selectedN1, setSelectedN1] = useState<CategoriaN1 | null>(null);
  const [selectedN2, setSelectedN2] = useState<CategoriaN2 | null>(null);

  // Search states
  const [searchN1, setSearchN1] = useState('');
  const [searchN2, setSearchN2] = useState('');
  const [searchN3, setSearchN3] = useState('');

  // Sorting states
  const [sortN1, setSortN1] = useState<'created_at' | 'nome'>('created_at');
  const [sortN2, setSortN2] = useState<'created_at' | 'nome'>('created_at');
  const [sortN3, setSortN3] = useState<'created_at' | 'nome'>('created_at');

  // Modal states
  const [formModal, setFormModal] = useState<{
    open: boolean;
    level: 1 | 2 | 3;
    item: any;
  }>({ open: false, level: 1, item: null });

  const [viewModal, setViewModal] = useState<{
    open: boolean;
    level: 1 | 2 | 3;
    item: any;
  }>({ open: false, level: 1, item: null });

  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    level: 1 | 2 | 3;
    item: any;
    showCascadeOption?: boolean;
  }>({ open: false, level: 1, item: null, showCascadeOption: false });

  useEffect(() => {
    loadCategoriasN1();
  }, [sortN1]);

  useEffect(() => {
    if (selectedN1) {
      loadCategoriasN2(selectedN1.id);
    } else {
      setCategoriasN2([]);
      setSelectedN2(null);
    }
  }, [selectedN1, sortN2]);

  useEffect(() => {
    if (selectedN2) {
      loadCategoriasN3(selectedN2.id);
    } else {
      setCategoriasN3([]);
    }
  }, [selectedN2, sortN3]);

  const loadCategoriasN1 = async () => {
    try {
      setLoading(true);
      const orderBy = sortN1 === 'created_at' ? 'created_at' : 'nome';
      const ascending = sortN1 === 'nome';

      const { data, error } = await supabase
        .from('categorias_n1')
        .select('*')
        .order(orderBy, { ascending });

      if (error) throw error;
      setCategoriasN1(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar categorias N1",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriasN2 = async (n1Id: string) => {
    try {
      const orderBy = sortN2 === 'created_at' ? 'created_at' : 'nome';
      const ascending = sortN2 === 'nome';

      const { data, error } = await supabase
        .from('categorias_n2')
        .select('*')
        .eq('id_categoria_n1', n1Id)
        .order(orderBy, { ascending });

      if (error) throw error;
      setCategoriasN2(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar categorias N2",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const loadCategoriasN3 = async (n2Id: string) => {
    try {
      const orderBy = sortN3 === 'created_at' ? 'created_at' : 'nome';
      const ascending = sortN3 === 'nome';

      const { data, error } = await supabase
        .from('categorias_n3')
        .select('*')
        .eq('id_categoria_n2', n2Id)
        .order(orderBy, { ascending });

      if (error) throw error;
      setCategoriasN3(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar categorias N3",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (cascadeDelete = false) => {
    try {
      const { level, item } = deleteModal;
      
      if (level === 1) {
        // Check if has children
        const { count } = await supabase
          .from('categorias_n2')
          .select('*', { count: 'exact', head: true })
          .eq('id_categoria_n1', item.id);

        if (count && count > 0 && !cascadeDelete) {
          toast({
            title: "Categoria possui subcategorias",
            description: `Esta categoria possui ${count} subcategoria(s) vinculada(s). Deseja excluir em cascata?`,
            variant: "destructive",
          });
          setDeleteModal({ ...deleteModal, showCascadeOption: true });
          return;
        }

        if (cascadeDelete && count && count > 0) {
          // Delete all N3 categories first
          const { data: categoriasN2 } = await supabase
            .from('categorias_n2')
            .select('id')
            .eq('id_categoria_n1', item.id);

          if (categoriasN2) {
            for (const catN2 of categoriasN2) {
              await supabase
                .from('categorias_n3')
                .delete()
                .eq('id_categoria_n2', catN2.id);
            }
          }

          // Delete all N2 categories
          await supabase
            .from('categorias_n2')
            .delete()
            .eq('id_categoria_n1', item.id);
        }

        const { error } = await supabase
          .from('categorias_n1')
          .delete()
          .eq('id', item.id);

        if (error) throw error;
        
        setSelectedN1(null);
        setSelectedN2(null);
        loadCategoriasN1();
      } else if (level === 2) {
        // Check if has children
        const { count } = await supabase
          .from('categorias_n3')
          .select('*', { count: 'exact', head: true })
          .eq('id_categoria_n2', item.id);

        if (count && count > 0 && !cascadeDelete) {
          toast({
            title: "Categoria possui subcategorias",
            description: `Esta categoria possui ${count} subcategoria(s) vinculada(s). Deseja excluir em cascata?`,
            variant: "destructive",
          });
          setDeleteModal({ ...deleteModal, showCascadeOption: true });
          return;
        }

        if (cascadeDelete && count && count > 0) {
          // Delete all N3 categories first
          await supabase
            .from('categorias_n3')
            .delete()
            .eq('id_categoria_n2', item.id);
        }

        const { error } = await supabase
          .from('categorias_n2')
          .delete()
          .eq('id', item.id);

        if (error) throw error;

        setSelectedN2(null);
        if (selectedN1) loadCategoriasN2(selectedN1.id);
      } else {
        const { error } = await supabase
          .from('categorias_n3')
          .delete()
          .eq('id', item.id);

        if (error) throw error;

        if (selectedN2) loadCategoriasN3(selectedN2.id);
      }

      toast({
        title: "Sucesso",
        description: cascadeDelete 
          ? "Categoria e suas subcategorias excluídas com sucesso!" 
          : "Categoria excluída com sucesso!",
      });

      setDeleteModal({ open: false, level: 1, item: null, showCascadeOption: false });
    } catch (error: any) {
      console.error('Delete error:', error);
      toast({
        title: "Erro ao excluir categoria",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleFormSuccess = () => {
    const { level } = formModal;
    
    if (level === 1) {
      loadCategoriasN1();
    } else if (level === 2 && selectedN1) {
      loadCategoriasN2(selectedN1.id);
    } else if (level === 3 && selectedN2) {
      loadCategoriasN3(selectedN2.id);
    }
  };

  const filteredN1 = categoriasN1.filter(item => 
    item.nome.toLowerCase().includes(searchN1.toLowerCase())
  );

  const filteredN2 = categoriasN2.filter(item => 
    item.nome.toLowerCase().includes(searchN2.toLowerCase())
  );

  const filteredN3 = categoriasN3.filter(item => 
    item.nome.toLowerCase().includes(searchN3.toLowerCase())
  );

  const getBreadcrumb = () => {
    const parts = [];
    if (selectedN1) {
      parts.push(`N1: ${selectedN1.nome}`);
    }
    if (selectedN2) {
      parts.push(`N2: ${selectedN2.nome}`);
    }
    return parts.join(' / ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg">Carregando categorias...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Gestão de Categorias</h1>
        <p className="text-muted-foreground">
          Gerencie categorias em 3 níveis hierárquicos
        </p>
      </div>

      {/* Breadcrumb */}
      {getBreadcrumb() && (
        <div className="bg-muted p-3 rounded-lg">
          <div className="text-sm font-medium">{getBreadcrumb()}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Nível 1 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Categorias Nível 1</CardTitle>
              <Button
                onClick={() => setFormModal({ open: true, level: 1, item: null })}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova N1
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={searchN1}
                    onChange={(e) => setSearchN1(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Select value={sortN1} onValueChange={(value: any) => setSortN1(value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Data</SelectItem>
                    <SelectItem value="nome">Nome</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredN1.map((item) => (
                  <div
                    key={item.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedN1?.id === item.id
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      setSelectedN1(item);
                      setSelectedN2(null);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{item.nome}</div>
                        {item.descricao && (
                          <div className="text-sm text-muted-foreground">
                            {item.descricao}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewModal({ open: true, level: 1, item });
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormModal({ open: true, level: 1, item });
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteModal({ open: true, level: 1, item });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nível 2 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Categorias Nível 2</CardTitle>
              <Button
                onClick={() => setFormModal({ open: true, level: 2, item: null })}
                size="sm"
                disabled={!selectedN1}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova N2
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedN1 ? (
              <div className="text-center text-muted-foreground py-8">
                Selecione uma categoria de Nível 1
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar..."
                      value={searchN2}
                      onChange={(e) => setSearchN2(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  <Select value={sortN2} onValueChange={(value: any) => setSortN2(value)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Data</SelectItem>
                      <SelectItem value="nome">Nome</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredN2.map((item) => (
                    <div
                      key={item.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedN2?.id === item.id
                          ? 'bg-primary/10 border-primary'
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => setSelectedN2(item)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{item.nome}</div>
                          {item.descricao && (
                            <div className="text-sm text-muted-foreground">
                              {item.descricao}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewModal({ open: true, level: 2, item });
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormModal({ open: true, level: 2, item });
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal({ open: true, level: 2, item });
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Nível 3 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Categorias Nível 3</CardTitle>
              <Button
                onClick={() => setFormModal({ open: true, level: 3, item: null })}
                size="sm"
                disabled={!selectedN2}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova N3
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!selectedN2 ? (
              <div className="text-center text-muted-foreground py-8">
                Selecione uma categoria de Nível 2
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar..."
                      value={searchN3}
                      onChange={(e) => setSearchN3(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  <Select value={sortN3} onValueChange={(value: any) => setSortN3(value)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at">Data</SelectItem>
                      <SelectItem value="nome">Nome</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredN3.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 border rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{item.nome}</div>
                          {item.descricao && (
                            <div className="text-sm text-muted-foreground">
                              {item.descricao}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewModal({ open: true, level: 3, item })}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormModal({ open: true, level: 3, item })}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteModal({ open: true, level: 3, item })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Form Modal */}
      <CategoriaFormModal
        open={formModal.open}
        onOpenChange={(open) => setFormModal({ ...formModal, open })}
        onSuccess={handleFormSuccess}
        level={formModal.level}
        categoria={formModal.item}
        selectedN1={selectedN1}
        selectedN2={selectedN2}
      />

      {/* View Modal */}
      <CategoriaViewModal
        open={viewModal.open}
        onOpenChange={(open) => setViewModal({ ...viewModal, open })}
        level={viewModal.level}
        categoria={viewModal.item}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ ...deleteModal, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a categoria "{deleteModal.item?.nome}"?
              {deleteModal.showCascadeOption && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
                  <strong>Atenção:</strong> Esta categoria possui subcategorias. Escolha uma opção:
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteModal({ open: false, level: 1, item: null, showCascadeOption: false })}>
              Cancelar
            </AlertDialogCancel>
            {deleteModal.showCascadeOption ? (
              <>
                <AlertDialogAction onClick={() => handleDelete(true)} className="bg-red-600 hover:bg-red-700">
                  Excluir Tudo (Cascata)
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction onClick={() => handleDelete(false)}>
                Excluir
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}